import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Home, Dna, Activity, Shield, Target, Zap, Users, Heart, Brain, TrendingUp, ArrowLeft } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { Link } from 'wouter';
import { LectureDownloader } from '@/components/LectureDownloader';

export default function AdvancedTherapeuticInnovations() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { isSignedIn } = useUser();

  if (!isSignedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">Please sign in to access this lecture.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        prevSlide();
      } else if (event.key === 'ArrowRight') {
        nextSlide();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide]);

  const slides = [
    // Slide 1: Title Slide
    {
      title: "Advanced Therapeutic Innovations: Peptides, Hormones, & Regenerative Medicine",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center space-y-8">
          <div className="flex items-center space-x-8 mb-8">
            <Dna className="w-20 h-20 text-blue-500" />
            <Activity className="w-20 h-20 text-purple-500" />
            <Shield className="w-20 h-20 text-green-500" />
          </div>
          
          <div className="space-y-6">
            <h1 className="text-4xl font-light text-gray-900 leading-tight">
              Advanced Therapeutic Innovations
            </h1>
            <h2 className="text-2xl text-blue-600 font-light max-w-4xl">
              Peptides, Hormones, & Regenerative Medicine
            </h2>
            <h3 className="text-lg text-gray-600 font-light max-w-4xl">
              A Longevity, Functional Medicine, and Health Optimization Perspective
            </h3>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mt-8 max-w-4xl">
            <p className="text-gray-700 font-light text-sm">
              Exploring the cutting-edge therapeutic modalities shaping the future of longevity medicine 
              and health optimization
            </p>
          </div>
        </div>
      )
    },

    // Slide 2: The Evolving Medical Paradigm: Healthspan Optimization
    {
      title: "The Evolving Medical Paradigm: Healthspan Optimization",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">A Shift in Focus</h3>
            <p className="text-sm text-gray-700">
              Medicine is evolving from solely managing disease to proactively optimizing <strong>healthspan</strong> – the period of healthy life.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-green-800 mb-3">Longevity Medicine</h3>
              <p className="text-sm text-gray-700">
                Aims to understand and decelerate the multifaceted processes of age-related decline, enhancing physiological resilience.
              </p>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-purple-800 mb-3">Functional Medicine Framework</h3>
              <p className="text-sm text-gray-700">
                Employs a systems-biology approach to identify and address root causes of dysfunction, restoring balance across interconnected biological networks.
              </p>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-yellow-800 mb-3">Goal</h3>
              <p className="text-sm text-gray-700">
                Not just to extend lifespan, but to improve the quality of life throughout aging. Advanced therapeutics (peptides, hormones, regenerative medicine) are key tools in this new frontier.
              </p>
            </div>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 500 250" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="250" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Paradigm Shift: Disease Management to Health Optimization
              </text>
              
              {/* Traditional approach box */}
              <rect x="50" y="50" width="180" height="80" fill="#fecaca" stroke="#ef4444" strokeWidth="2" rx="8" />
              <text x="140" y="75" textAnchor="middle" className="text-sm font-medium fill-gray-700">Traditional Medicine</text>
              <text x="140" y="95" textAnchor="middle" className="text-xs fill-gray-600">• Disease management</text>
              <text x="140" y="110" textAnchor="middle" className="text-xs fill-gray-600">• Reactive approach</text>
              <text x="140" y="125" textAnchor="middle" className="text-xs fill-gray-600">• Symptom treatment</text>
              
              {/* Arrow */}
              <path d="M 240 90 L 270 90" stroke="#6b7280" strokeWidth="3" markerEnd="url(#arrowGray)" />
              
              {/* New paradigm box */}
              <rect x="280" y="50" width="180" height="80" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" rx="8" />
              <text x="370" y="75" textAnchor="middle" className="text-sm font-medium fill-gray-700">Longevity Medicine</text>
              <text x="370" y="95" textAnchor="middle" className="text-xs fill-gray-600">• Health optimization</text>
              <text x="370" y="110" textAnchor="middle" className="text-xs fill-gray-600">• Proactive approach</text>
              <text x="370" y="125" textAnchor="middle" className="text-xs fill-gray-600">• Root cause focus</text>
              
              {/* Benefits section */}
              <rect x="150" y="160" width="200" height="60" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1" rx="5" />
              <text x="250" y="180" textAnchor="middle" className="text-sm font-medium fill-gray-700">Advanced Therapeutics</text>
              <text x="250" y="200" textAnchor="middle" className="text-xs fill-gray-600">Peptides • Hormones • Regenerative Medicine</text>
              
              <defs>
                <marker id="arrowGray" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#6b7280" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 3: Overview of Advanced Therapeutics
    {
      title: "Overview of Advanced Therapeutics",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <Dna className="w-6 h-6 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-800">Peptides</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Short chains of amino acids acting as precise signaling molecules</li>
                    <li>• Offer targeted physiological responses, often with favorable side-effect profiles</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <Activity className="w-6 h-6 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800">Hormones</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• The body's foundational chemical messengers orchestrating vast physiological processes</li>
                    <li>• Optimal balance is paramount for health and vitality throughout life</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <Shield className="w-6 h-6 text-orange-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-orange-800">Regenerative Medicine</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Stem cells and exosomes offer tools to repair, replace, and rejuvenate damaged or aging tissues</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Convergence & Personalization</h3>
            <ul className="text-sm space-y-2 text-gray-700">
              <li>• Increasing use of multi-modal strategies addressing aging from multiple angles</li>
              <li>• Success depends on advanced biomarker tracking and highly personalized, "N-of-1" approaches</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 450 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="225" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Convergence of Advanced Therapeutics
              </text>
              
              {/* Three therapeutic categories */}
              <rect x="30" y="50" width="100" height="60" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" rx="8" />
              <text x="80" y="75" textAnchor="middle" className="text-xs font-medium fill-gray-700">Peptides</text>
              <text x="80" y="90" textAnchor="middle" className="text-xs fill-gray-600">Precise Signaling</text>
              
              <rect x="175" y="50" width="100" height="60" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" rx="8" />
              <text x="225" y="75" textAnchor="middle" className="text-xs font-medium fill-gray-700">Hormones</text>
              <text x="225" y="90" textAnchor="middle" className="text-xs fill-gray-600">Systemic Balance</text>
              
              <rect x="320" y="50" width="100" height="60" fill="#fed7aa" stroke="#f97316" strokeWidth="2" rx="8" />
              <text x="370" y="75" textAnchor="middle" className="text-xs font-medium fill-gray-700">Regenerative</text>
              <text x="370" y="90" textAnchor="middle" className="text-xs fill-gray-600">Tissue Repair</text>
              
              {/* Convergence arrows */}
              <path d="M 130 80 L 175 80" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              <path d="M 275 80 L 320 80" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              
              {/* Personalization foundation */}
              <rect x="125" y="140" width="200" height="40" fill="#f3e8ff" stroke="#a855f7" strokeWidth="2" rx="5" />
              <text x="225" y="155" textAnchor="middle" className="text-sm font-medium fill-gray-700">Multi-Modal Strategies</text>
              <text x="225" y="170" textAnchor="middle" className="text-xs fill-gray-600">Biomarker-Guided Personalization</text>
              
              <defs>
                <marker id="arrowGray" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                  <polygon points="0 0, 6 2, 0 4" fill="#6b7280" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 4: Peptide Therapeutics: Precision Signaling
    {
      title: "Peptide Therapeutics: Precision Signaling",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Definition</h3>
            <p className="text-sm text-gray-700">
              Short chains (typically 3-50) of amino acids functioning as highly specific signaling molecules.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">History</h3>
            <p className="text-sm text-gray-700">
              Insulin (1921) was the pioneering peptide therapeutic.
            </p>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Mechanisms of Action (Examples)</h3>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <h4 className="font-medium text-purple-700 text-sm">Signal Peptides</h4>
                <p className="text-xs text-gray-600">Bind cell surface receptors (e.g., GHRH analogs stimulating GH release)</p>
              </div>
              <div>
                <h4 className="font-medium text-purple-700 text-sm">Carrier Peptides</h4>
                <p className="text-xs text-gray-600">Transport molecules (e.g., GHK-Cu transporting copper for wound healing)</p>
              </div>
              <div>
                <h4 className="font-medium text-purple-700 text-sm">Neurotransmitter/Enzyme Inhibitors</h4>
                <p className="text-xs text-gray-600">Modulate neuronal activity or inhibit enzymes (e.g., KPV interfering with NF-κB)</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Advantages in Longevity Medicine</h3>
            <ul className="text-sm space-y-2 text-gray-700">
              <li>• Specificity allows "fine-tuning" of dysregulated pathways</li>
              <li>• Potentially more favorable side-effect profiles than broader-acting drugs</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 450 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="225" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Peptide Mechanisms of Action
              </text>
              
              {/* Signal Peptides */}
              <rect x="30" y="50" width="120" height="80" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" rx="8" />
              <text x="90" y="70" textAnchor="middle" className="text-xs font-medium fill-gray-700">Signal Peptides</text>
              <text x="90" y="85" textAnchor="middle" className="text-xs fill-gray-600">Bind receptors</text>
              <text x="90" y="100" textAnchor="middle" className="text-xs fill-gray-600">Initiate cascades</text>
              <text x="90" y="115" textAnchor="middle" className="text-xs fill-blue-700">Example: GHRH</text>
              
              {/* Carrier Peptides */}
              <rect x="165" y="50" width="120" height="80" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" rx="8" />
              <text x="225" y="70" textAnchor="middle" className="text-xs font-medium fill-gray-700">Carrier Peptides</text>
              <text x="225" y="85" textAnchor="middle" className="text-xs fill-gray-600">Transport molecules</text>
              <text x="225" y="100" textAnchor="middle" className="text-xs fill-gray-600">Deliver compounds</text>
              <text x="225" y="115" textAnchor="middle" className="text-xs fill-green-700">Example: GHK-Cu</text>
              
              {/* Inhibitor Peptides */}
              <rect x="300" y="50" width="120" height="80" fill="#fed7aa" stroke="#f97316" strokeWidth="2" rx="8" />
              <text x="360" y="70" textAnchor="middle" className="text-xs font-medium fill-gray-700">Inhibitor Peptides</text>
              <text x="360" y="85" textAnchor="middle" className="text-xs fill-gray-600">Modulate activity</text>
              <text x="360" y="100" textAnchor="middle" className="text-xs fill-gray-600">Block pathways</text>
              <text x="360" y="115" textAnchor="middle" className="text-xs fill-orange-700">Example: KPV</text>
              
              {/* Advantages */}
              <rect x="125" y="150" width="200" height="40" fill="#f3e8ff" stroke="#a855f7" strokeWidth="2" rx="5" />
              <text x="225" y="165" textAnchor="middle" className="text-sm font-medium fill-gray-700">Precision & Specificity</text>
              <text x="225" y="180" textAnchor="middle" className="text-xs fill-gray-600">Fine-tuning dysregulated pathways</text>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 5: Growth Hormone Secretagogues (GHS): Modulating the GH/IGF-1 Axis
    {
      title: "Growth Hormone Secretagogues (GHS): Modulating the GH/IGF-1 Axis",
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Rationale</h3>
            <p className="text-sm text-gray-700">
              Address "somatopause" – age-related decline in Growth Hormone (GH) and IGF-1, contributing to adverse body composition changes, low energy.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-blue-800 mb-3">GHRH Analogs (e.g., Sermorelin, CJC-1295 w/o DAC)</h3>
              <ul className="text-sm space-y-1 text-gray-700">
                <li>• <strong>Mechanism:</strong> Mimic GHRH, stimulate pituitary for pulsatile GH release → ↑IGF-1</li>
                <li>• <strong>Effects:</strong> ↑Lean mass, ↓fat, improved sleep, skin</li>
                <li>• <strong>Clinical:</strong> Daily SC. Monitor IGF-1 (aim for youthful physiological range)</li>
              </ul>
            </div>
            
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-green-800 mb-3">GHRP Analogs (e.g., Ipamorelin)</h3>
              <ul className="text-sm space-y-1 text-gray-700">
                <li>• <strong>Mechanism:</strong> Ghrelin mimetics, bind GHSR-1a on pituitary, synergistic with GHRH</li>
                <li>• <strong>Ipamorelin:</strong> Favored for high selectivity for GH release, less impact on cortisol/hunger</li>
              </ul>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-purple-800 mb-3">Tesamorelin</h3>
              <ul className="text-sm space-y-1 text-gray-700">
                <li>• <strong>Mechanism:</strong> Stabilized GHRH analog. FDA-approved for HIV lipodystrophy (visceral fat reduction)</li>
                <li>• <strong>Potential:</strong> Cognitive benefits, improved lipids</li>
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Caution</h3>
            <p className="text-sm text-gray-700">
              Avoid supraphysiological IGF-1 levels.
            </p>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <defs>
                <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#004d40"/>
                </marker>
                <marker id="arrowheadstim" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#d32f2f"/>
                </marker>
              </defs>

              <rect x="150" y="10" width="100" height="30" fill="#e0f7fa" stroke="#00796b" strokeWidth="1.5" rx="5" ry="5"/>
              <text x="200" y="30" textAnchor="middle" className="text-xs font-medium fill-gray-700">Hypothalamus</text>
              <text x="200" y="45" textAnchor="middle" className="text-xs fill-gray-700">(GHRH)</text>

              <line x1="200" y1="50" x2="200" y2="80" stroke="#004d40" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>

              <rect x="150" y="90" width="100" height="30" fill="#e0f7fa" stroke="#00796b" strokeWidth="1.5" rx="5" ry="5"/>
              <text x="200" y="110" textAnchor="middle" className="text-xs font-medium fill-gray-700">Pituitary</text>
              <text x="200" y="125" textAnchor="middle" className="text-xs fill-gray-700">(GH)</text>

              <line x1="200" y1="130" x2="200" y2="160" stroke="#004d40" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>

              <rect x="150" y="170" width="100" height="30" fill="#e0f7fa" stroke="#00796b" strokeWidth="1.5" rx="5" ry="5"/>
              <text x="200" y="190" textAnchor="middle" className="text-xs font-medium fill-gray-700">Liver / Tissues</text>
              <text x="200" y="205" textAnchor="middle" className="text-xs fill-gray-700">(IGF-1)</text>

              <rect x="10" y="90" width="120" height="30" fill="#ffe0b2" stroke="#e65100" strokeWidth="1.5" rx="5" ry="5"/>
              <text x="70" y="110" textAnchor="middle" className="text-xs font-medium fill-gray-700">GHRH Analogs</text>
              <text x="70" y="122" textAnchor="middle" className="text-xs fill-gray-700">(Sermorelin, CJC-1295)</text>
              <line x1="130" y1="105" x2="150" y2="105" stroke="#d32f2f" strokeWidth="1.5" markerEnd="url(#arrowheadstim)" strokeDasharray="4 2"/>

              <rect x="270" y="70" width="120" height="30" fill="#ffe0b2" stroke="#e65100" strokeWidth="1.5" rx="5" ry="5"/>
              <text x="330" y="90" textAnchor="middle" className="text-xs font-medium fill-gray-700">GHRP Analogs</text>
              <text x="330" y="102" textAnchor="middle" className="text-xs fill-gray-700">(Ipamorelin)</text>
              <line x1="250" y1="85" x2="270" y2="85" stroke="#d32f2f" strokeWidth="1.5" markerEnd="url(#arrowheadstim)" strokeDasharray="4 2"/>
              
              <rect x="270" y="110" width="120" height="30" fill="#ffe0b2" stroke="#e65100" strokeWidth="1.5" rx="5" ry="5"/>
              <text x="330" y="130" textAnchor="middle" className="text-xs font-medium fill-gray-700">Tesamorelin</text>
              <line x1="250" y1="125" x2="270" y2="125" stroke="#d32f2f" strokeWidth="1.5" markerEnd="url(#arrowheadstim)" strokeDasharray="4 2"/>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 6: Peptide Therapeutics
    {
      title: "Peptides: Precision Signaling Molecules",
      content: (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Definition</h3>
            <p className="text-sm text-gray-700">
              Short chains of amino acids (typically 3-50 residues) acting as highly specific signaling molecules.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-blue-800 mb-3">Specificity Advantage</h3>
              <p className="text-sm text-gray-700">
                Allows targeted actions with potentially fewer side effects than larger drug molecules 
                (reduced off-target interactions).
              </p>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-purple-800 mb-3">Historical Context</h3>
              <p className="text-sm text-gray-700">
                Insulin (1921) was the pioneering peptide therapeutic.
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Relevance to Aging</h3>
            <p className="text-sm text-gray-700">
              Ideal for "fine-tuning" physiological pathways dysregulated during aging, offering 
              nuanced adjustments to restore balance.
            </p>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-2xl">
              <text x="200" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Peptide Signaling Mechanism
              </text>
              
              {/* Cell membrane */}
              <ellipse cx="200" cy="120" rx="80" ry="50" fill="#f3f4f6" stroke="#6b7280" strokeWidth="2" />
              <text x="200" y="125" textAnchor="middle" className="text-xs font-medium fill-gray-700">Target Cell</text>
              
              {/* Peptide chain */}
              <g>
                <circle cx="60" cy="80" r="6" fill="#22c55e" />
                <circle cx="75" cy="80" r="6" fill="#3b82f6" />
                <circle cx="90" cy="80" r="6" fill="#f59e0b" />
                <circle cx="105" cy="80" r="6" fill="#ef4444" />
                <line x1="66" y1="80" x2="69" y2="80" stroke="#6b7280" strokeWidth="2" />
                <line x1="81" y1="80" x2="84" y2="80" stroke="#6b7280" strokeWidth="2" />
                <line x1="96" y1="80" x2="99" y2="80" stroke="#6b7280" strokeWidth="2" />
                <text x="82" y="65" textAnchor="middle" className="text-xs font-medium fill-gray-700">Peptide Chain</text>
              </g>
              
              {/* Receptor */}
              <rect x="115" y="95" width="15" height="25" fill="#a855f7" rx="3" />
              <text x="122" y="85" textAnchor="middle" className="text-xs font-medium fill-gray-700">Receptor</text>
              
              {/* Binding arrow */}
              <path d="M 105 85 Q 115 90 122 95" fill="none" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowGray)" />
              
              {/* Intracellular signal */}
              <g>
                <circle cx="180" cy="140" r="4" fill="#fbbf24" />
                <circle cx="200" cy="150" r="4" fill="#fbbf24" />
                <circle cx="220" cy="140" r="4" fill="#fbbf24" />
                <text x="200" y="175" textAnchor="middle" className="text-xs font-medium fill-gray-700">Intracellular Signal</text>
              </g>
              
              {/* Signal pathway arrow */}
              <path d="M 130 110 Q 150 125 180 135" fill="none" stroke="#fbbf24" strokeWidth="2" markerEnd="url(#arrowYellow)" />
              
              <defs>
                <marker id="arrowGray" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                  <polygon points="0 0, 6 2, 0 4" fill="#6b7280" />
                </marker>
                <marker id="arrowYellow" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                  <polygon points="0 0, 6 2, 0 4" fill="#fbbf24" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 7: Metabolic & Other Notable Peptides
    {
      title: "Metabolic & Other Notable Peptides",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">GLP-1 Agonists (e.g., Semaglutide, Liraglutide)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Mechanism:</strong> ↑Insulin (glucose-dependent), ↓glucagon, delayed gastric emptying, CNS appetite suppression</li>
              <li>• <strong>Applications:</strong> T2DM, obesity, CV protection. Emerging: neuroprotection</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">GHK-Cu (Copper Peptide)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Mechanism:</strong> Stimulates ECM (collagen, elastin), modulates MMPs, "resets" gene expression towards healthier state</li>
              <li>• <strong>Applications:</strong> Skin regeneration (topical), wound healing</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Mitochondria-Derived Peptides (MDPs)</h3>
            <p className="text-sm text-gray-700 mb-3"><strong>Significance:</strong> Mitochondria as endocrine-like organelles</p>
            
            <div className="grid grid-cols-1 gap-3">
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-purple-700 text-sm">MOTS-c</h4>
                <p className="text-xs text-gray-600">"Exercise-mimetic"; enhances insulin sensitivity via Folate-AICAR-AMPK pathway</p>
                <p className="text-xs text-gray-600"><strong>Applications:</strong> Metabolic dysfunction, insulin resistance, cognitive function</p>
              </div>
              
              <div className="bg-white p-3 rounded border">
                <h4 className="font-medium text-purple-700 text-sm">Humanin (HN)</h4>
                <p className="text-xs text-gray-600">Cytoprotective, anti-apoptotic; interacts with IGFBP-3, activates ERK, AKT, STAT3</p>
                <p className="text-xs text-gray-600">Plasma levels decline with age, higher levels linked to longevity in mice</p>
                <p className="text-xs text-gray-600"><strong>Applications:</strong> Potential for T2DM, AD, CVD</p>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Slide 8: Hormonal Optimization: Restoring Balance  
    {
      title: "Hormonal Optimization: Restoring Balance",
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Aging & Hormonal Decline</h3>
            <p className="text-sm text-gray-700">
              Progressive decline in multiple hormones contributes to the aging phenotype.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Goal</h3>
            <p className="text-sm text-gray-700">
              Restore <strong>optimal physiological levels and signaling</strong>, not just "normal" lab ranges, to improve healthspan.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-green-800 mb-3">GH Axis</h3>
              <ul className="text-sm space-y-1 text-gray-700">
                <li>• Somatopause (↓GH/IGF-1). GHS preferred over rhGH (more physiological, pulsatile release, fewer side effects like edema, arthralgia)</li>
                <li>• <strong>Caution:</strong> Avoid supraphysiological IGF-1</li>
              </ul>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-purple-800 mb-3">Sex Hormones</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-purple-700 text-sm">Testosterone (Men & Women)</h4>
                  <p className="text-xs text-gray-600">Benefits for libido, muscle mass/strength, bone density, mood, energy. Low T in men linked to ↑CVD/mortality</p>
                </div>
                <div>
                  <h4 className="font-medium text-purple-700 text-sm">Estrogen & Progesterone (Women - MHT)</h4>
                  <p className="text-xs text-gray-600">Benefits: Effective for vasomotor symptoms (VMS), osteoporosis prevention</p>
                  <p className="text-xs text-gray-600">WHI Re-evaluation: Risks depend on hormone type, route, timing of initiation ("timing hypothesis")</p>
                  <p className="text-xs text-gray-600"><strong>Clinical Pearl:</strong> Bioidentical hormones (e.g., transdermal estradiol, oral micronized progesterone) generally preferred for safety and physiology</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Slide 9: Thyroid, Adrenal Hormones & Melatonin
    {
      title: "Thyroid, Adrenal Hormones & Melatonin",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Thyroid Hormones (T3, T4)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Essential for metabolism, energy, cognitive function</li>
              <li>• Goal: Achieve euthyroidism. Resolve symptoms, normalize TSH, free T3, free T4</li>
              <li>• Consider T4/T3 combo for select patients. Note: Overt hypothyroidism is detrimental; some studies link mildly lower function to longevity (nuance needed)</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Adrenal Hormones</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-green-700 text-sm">Cortisol</h4>
                <p className="text-xs text-gray-600">Chronic elevation (aging, stress) is detrimental (e.g., hippocampal/PFC damage, impaired stress recovery)</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">DHEA</h4>
                <p className="text-xs text-gray-600">Levels decline significantly with age. Precursor to sex hormones. Mixed results from human supplementation trials for body comp/strength</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Pregnenolone</h4>
                <p className="text-xs text-gray-600">Neurosteroid, "mother hormone." Benefits for learning, memory, mood. Neuroprotective, anti-inflammatory</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Melatonin</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Pineal hormone, production declines with age</li>
              <li>• Multifaceted: Regulates sleep, potent antioxidant, anti-inflammatory, immunomodulator, mitochondrial protector</li>
              <li>• Applications: Sleep disorders; promising for neurodegenerative diseases (AD, PD)</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 10: Regenerative Medicine: Stem Cells & Exosomes
    {
      title: "Regenerative Medicine: Stem Cells & Exosomes",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Stem Cells (Mesenchymal Stem Cells - MSCs)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Mechanism:</strong> Multipotent, self-renewal. Therapeutic effects largely via <strong>paracrine actions</strong> – secretome (trophic factors, cytokines, exosomes) promoting anti-inflammatory, pro-angiogenic, immunomodulatory effects</li>
              <li>• <strong>Applications:</strong> Osteoarthritis, CVD, autoimmune conditions, wound healing</li>
              <li>• <strong>Challenges:</strong> Senescence with age/culture, heterogeneity</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Exosomes</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Definition:</strong> Nano-sized extracellular vesicles (30-150nm) containing bioactive cargo (proteins, lipids, miRNAs, mRNAs)</li>
              <li>• <strong>Advantages:</strong> Cell-free therapy, lower immunogenicity risk, targeted delivery potential</li>
              <li>• <strong>Sources:</strong> MSCs, neural stem cells, immune cells, etc.</li>
              <li>• <strong>Applications:</strong> Tissue repair, neuroregeneration, immune modulation</li>
            </ul>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 450 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="225" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Regenerative Medicine Approaches
              </text>
              
              {/* MSCs */}
              <circle cx="120" cy="80" r="35" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" />
              <text x="120" y="75" textAnchor="middle" className="text-xs font-medium fill-gray-700">MSCs</text>
              <text x="120" y="85" textAnchor="middle" className="text-xs fill-gray-600">Multipotent</text>
              
              {/* Exosomes */}
              <circle cx="330" cy="80" r="35" fill="#dcfce7" stroke="#22c55e" strokeWidth="2" />
              <text x="330" y="75" textAnchor="middle" className="text-xs font-medium fill-gray-700">Exosomes</text>
              <text x="330" y="85" textAnchor="middle" className="text-xs fill-gray-600">Cell-free</text>
              
              {/* Paracrine effects */}
              <rect x="180" y="50" width="90" height="25" fill="#fed7aa" stroke="#f97316" strokeWidth="1" rx="3" />
              <text x="225" y="67" textAnchor="middle" className="text-xs fill-gray-700">Paracrine Effects</text>
              
              {/* Arrows */}
              <path d="M 155 80 L 180 62" stroke="#3b82f6" strokeWidth="2" markerEnd="url(#arrowBlue4)" />
              <path d="M 295 80 L 270 62" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowGreen4)" />
              
              {/* Therapeutic outcomes */}
              <rect x="50" y="140" width="100" height="40" fill="#f3e8ff" stroke="#a855f7" strokeWidth="1" rx="5" />
              <text x="100" y="155" textAnchor="middle" className="text-xs font-medium fill-gray-700">Tissue Repair</text>
              <text x="100" y="170" textAnchor="middle" className="text-xs fill-gray-600">Anti-inflammatory</text>
              
              <rect x="175" y="140" width="100" height="40" fill="#f3e8ff" stroke="#a855f7" strokeWidth="1" rx="5" />
              <text x="225" y="155" textAnchor="middle" className="text-xs font-medium fill-gray-700">Immunomodulation</text>
              <text x="225" y="170" textAnchor="middle" className="text-xs fill-gray-600">Angiogenesis</text>
              
              <rect x="300" y="140" width="100" height="40" fill="#f3e8ff" stroke="#a855f7" strokeWidth="1" rx="5" />
              <text x="350" y="155" textAnchor="middle" className="text-xs font-medium fill-gray-700">Regeneration</text>
              <text x="350" y="170" textAnchor="middle" className="text-xs fill-gray-600">Targeted delivery</text>
              
              <defs>
                <marker id="arrowBlue4" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#3b82f6" />
                </marker>
                <marker id="arrowGreen4" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#22c55e" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      )
    },

    // Slide 11: Biochemical Reserves: Plasmalogens
    {
      title: "Biochemical Reserves: Plasmalogens",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Structure</h3>
            <p className="text-sm text-gray-700">
              Unique glycerophospholipids with a vinyl-ether bond at sn-1.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Key Functions</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Sacrificial endogenous antioxidants</strong> (protecting other lipids/proteins)</li>
              <li>• Essential for membrane fluidity, stability, lipid rafts</li>
              <li>• Crucial for vesicle formation, neurotransmitter release, myelin integrity</li>
            </ul>
          </div>

          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">Age-Related Decline & Disease</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Levels decline significantly with age (esp. post-50)</li>
              <li>• Depletion prominent in neurodegenerative diseases (Alzheimer's, Parkinson's)</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Restoration Strategies (Oral Precursors)</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-purple-700 text-sm">Omega-3 (DHA) Plasmalogen Precursors (e.g., ProdromeNeuro)</h4>
                <p className="text-xs text-gray-600">Target gray matter, synapses; support cognitive function</p>
              </div>
              <div>
                <h4 className="font-medium text-purple-700 text-sm">Omega-9 (Oleic Acid) Plasmalogen Precursors (e.g., ProdromeGlia)</h4>
                <p className="text-xs text-gray-600">Target white matter, myelin; support glial cell function</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Clinical Pearl</h3>
            <p className="text-sm text-gray-700">
              Targeted supplementation based on specific fatty acid needs and desired neurological outcome (e.g., cognition vs. myelin support).
            </p>
          </div>
        </div>
      )
    },

    // Slide 12: Interplay with Core Aging Pathways: Introduction
    {
      title: "Interplay with Core Aging Pathways: Introduction",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <p className="text-sm text-gray-700">
              Aging is not driven by a single mechanism but by a complex interplay of numerous interconnected cellular signaling pathways.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <p className="text-sm text-gray-700">
              Dysregulation of these pathways (due to genetics, environment, lifestyle) contributes to chronic disease and accelerated aging.
            </p>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <p className="text-sm text-gray-700">
              The advanced therapeutics discussed (peptides, hormones, regenerative modalities) often exert their benefits by modulating one or more of these fundamental pathways.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Key Pathways We Will Discuss</h3>
            <ol className="text-sm space-y-1 text-gray-700 list-decimal list-inside">
              <li>Insulin/IGF-1 Signaling (IIS)</li>
              <li>mTOR (Mechanistic Target of Rapamycin) Signaling</li>
              <li>AMPK (AMP-Activated Protein Kinase) Signaling</li>
              <li>Sirtuins/NAD⁺ Pathway</li>
              <li>NF-κB (Nuclear Factor kappa-B) Signaling</li>
            </ol>
          </div>

          <div className="flex justify-center">
            <svg viewBox="0 0 450 200" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-3xl">
              <text x="225" y="20" textAnchor="middle" className="text-sm font-medium fill-gray-700">
                Core Aging Pathways Interconnection
              </text>
              
              {/* Central hub */}
              <circle cx="225" cy="100" r="30" fill="#f3e8ff" stroke="#a855f7" strokeWidth="2" />
              <text x="225" y="100" textAnchor="middle" className="text-xs font-medium fill-gray-700">Advanced</text>
              <text x="225" y="110" textAnchor="middle" className="text-xs font-medium fill-gray-700">Therapeutics</text>
              
              {/* Pathways around the hub */}
              <rect x="50" y="50" width="80" height="30" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1" rx="5" />
              <text x="90" y="70" textAnchor="middle" className="text-xs fill-gray-700">IIS Pathway</text>
              
              <rect x="320" y="50" width="80" height="30" fill="#dcfce7" stroke="#22c55e" strokeWidth="1" rx="5" />
              <text x="360" y="70" textAnchor="middle" className="text-xs fill-gray-700">mTOR</text>
              
              <rect x="50" y="150" width="80" height="30" fill="#fed7aa" stroke="#f97316" strokeWidth="1" rx="5" />
              <text x="90" y="170" textAnchor="middle" className="text-xs fill-gray-700">AMPK</text>
              
              <rect x="320" y="150" width="80" height="30" fill="#fce7f3" stroke="#ec4899" strokeWidth="1" rx="5" />
              <text x="360" y="170" textAnchor="middle" className="text-xs fill-gray-700">Sirtuins/NAD+</text>
              
              <rect x="172" y="30" width="80" height="30" fill="#f3f4f6" stroke="#6b7280" strokeWidth="1" rx="5" />
              <text x="212" y="50" textAnchor="middle" className="text-xs fill-gray-700">NF-κB</text>
              
              {/* Connection lines */}
              <line x1="130" y1="65" x2="195" y2="85" stroke="#6b7280" strokeWidth="1" />
              <line x1="320" y1="65" x2="255" y2="85" stroke="#6b7280" strokeWidth="1" />
              <line x1="130" y1="165" x2="195" y2="115" stroke="#6b7280" strokeWidth="1" />
              <line x1="320" y1="165" x2="255" y2="115" stroke="#6b7280" strokeWidth="1" />
              <line x1="212" y1="60" x2="225" y2="70" stroke="#6b7280" strokeWidth="1" />
            </svg>
          </div>
        </div>
      )
    },

    // Slide 13: Insulin/IGF-1 Signaling (IIS) & Therapeutic Interactions
    {
      title: "Insulin/IGF-1 Signaling (IIS) & Therapeutic Interactions",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Overview</h3>
            <p className="text-sm text-gray-700">
              Conserved nutrient-sensing pathway regulating growth, metabolism, lifespan. Reduced IIS can extend lifespan in model organisms. Dysregulation linked to metabolic syndrome, T2DM, aging.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Therapeutic Interactions</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-green-700 text-sm">GHS (CJC-1295, Ipamorelin, Tesamorelin)</h4>
                <p className="text-xs text-gray-600">↑GH → ↑IGF-1 → anabolic effects. Clinical: Restore youthful, physiological IGF-1. Monitor IGF-1.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">GLP-1 RAs (Semaglutide, Liraglutide)</h4>
                <p className="text-xs text-gray-600">Insulinotropic; improve glucose control. Clinical: Monitor glucose, HbA1c, HOMA-IR.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Humanin</h4>
                <p className="text-xs text-gray-600">Interacts with IGFBP-3, modulating IGF-1 activity; cytoprotective.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Estrogen</h4>
                <p className="text-xs text-gray-600">Improves insulin sensitivity (via ERα → PI3K/Akt → ↓Foxo1).</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Clinical Relevance</h3>
            <p className="text-sm text-gray-700">
              Balance GHS anabolic benefits with risks of excessive/chronic IIS. Brain-specific IIS modulation (GLP-1 RAs, Humanin) for neuroprotection shows promise.
            </p>
          </div>
        </div>
      )
    },

    // Slide 14: mTOR Signaling & Therapeutic Interactions
    {
      title: "mTOR Signaling & Therapeutic Interactions",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Overview</h3>
            <p className="text-sm text-gray-700">
              Central regulator of cell growth, proliferation, protein synthesis, autophagy. Integrates nutrient & growth factor signals. mTOR Complex 1 (mTORC1) overactivation is linked to accelerated aging.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Therapeutic Interactions</h3>
            <div className="space-y-2">
              <div>
                <h4 className="font-medium text-green-700 text-sm">GHS (via IGF-1)</h4>
                <p className="text-xs text-gray-600">Can activate mTOR (anabolic for muscle). Pulsatile GH may avoid chronic mTORC1 issues.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">GLP-1 RAs (e.g., Liraglutide)</h4>
                <p className="text-xs text-gray-600">May activate AMPK, which can inhibit mTORC1.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Testosterone</h4>
                <p className="text-xs text-gray-600">Activates mTOR in muscle, mediating hypertrophy.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Estrogen</h4>
                <p className="text-xs text-gray-600">Can activate mTORC1. ERα-mTORC1 positive feedback loop has implications (e.g., hormone-sensitive cancers).</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Melatonin</h4>
                <p className="text-xs text-gray-600">May suppress mTOR signaling, potentially via AMPK.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">MSCs</h4>
                <p className="text-xs text-gray-600">mTOR inhibition can delay MSC senescence, maintain "stemness."</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Clinical Relevance</h3>
            <p className="text-sm text-gray-700">
              Dual nature – mTOR essential for anabolism but chronic overactivation is pro-aging. Context is key: acute, controlled activation vs. chronic dampening.
            </p>
          </div>
        </div>
      )
    },

    // Slide 15: AMPK Signaling & Therapeutic Interactions
    {
      title: "AMPK Signaling & Therapeutic Interactions",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Overview</h3>
            <p className="text-sm text-gray-700">
              AMP-activated protein kinase (AMPK) is a crucial cellular energy sensor. Activated by low energy (↑AMP/ATP). Promotes catabolism (e.g., fatty acid oxidation), mitochondrial biogenesis. Generally pro-longevity. AMPK responsiveness declines with age.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Therapeutic Interactions (Activators)</h3>
            <div className="space-y-2">
              <div>
                <h4 className="font-medium text-green-700 text-sm">GLP-1 RAs (e.g., Liraglutide, Exenatide)</h4>
                <p className="text-xs text-gray-600">Activate AMPK, contributing to metabolic benefits.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">MOTS-c</h4>
                <p className="text-xs text-gray-600">Activates AMPK (via Folate-AICAR pathway), enhancing glucose metabolism.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Humanin</h4>
                <p className="text-xs text-gray-600">Activates AMPK, contributing to cytoprotective effects.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Melatonin</h4>
                <p className="text-xs text-gray-600">May regulate AMPK (context-dependent).</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">MSCs</h4>
                <p className="text-xs text-gray-600">AMPK activation can delay MSC senescence.</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Clinical Relevance</h3>
            <p className="text-sm text-gray-700">
              Tissue-specific effects (e.g., T3 activates AMPK in muscle, reduces in liver/hypothalamus). Age-related decline in AMPK responsiveness is a therapeutic challenge.
            </p>
          </div>
        </div>
      )
    },

    // Slide 16: Sirtuins/NAD⁺ Pathway & Therapeutic Interactions
    {
      title: "Sirtuins/NAD⁺ Pathway & Therapeutic Interactions",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Overview</h3>
            <p className="text-sm text-gray-700">
              Sirtuins (SIRT1-7) are NAD⁺-dependent deacetylases crucial for cellular health, stress response, DNA repair, metabolism, inflammation, mitochondrial function. NAD⁺ levels (and thus sirtuin activity) decline with age.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Therapeutic Interactions</h3>
            <div className="space-y-2">
              <div>
                <h4 className="font-medium text-green-700 text-sm">GLP-1 RAs (Liraglutide)</h4>
                <p className="text-xs text-gray-600">Can ↑SIRT1 expression (e.g., in diabetic bone models).</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">MOTS-c</h4>
                <p className="text-xs text-gray-600">Can ↑cellular NAD⁺ levels; metabolic benefits partly SIRT1-mediated.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Melatonin</h4>
                <p className="text-xs text-gray-600">Stimulates SIRT1 expression/activity, mediating many of its protective effects.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">SIRT1-Overexpressing ADSC Exosomes</h4>
                <p className="text-xs text-gray-600">Innovative strategy to deliver functional SIRT1 protein to target cells, promoting angiogenesis.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">MSCs</h4>
                <p className="text-xs text-gray-600">NAD⁺ precursor (NMN) supplementation can improve senescent MSC health by boosting SIRT3.</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Clinical Relevance</h3>
            <p className="text-sm text-gray-700">
              NAD⁺ precursors (NMN, NR) are foundational to "refuel" sirtuins. Innovative delivery (SIRT1-exosomes) emerging.
            </p>
          </div>
        </div>
      )
    },

    // Slide 17: NF-κB Signaling & Therapeutic Interactions
    {
      title: "NF-κB Signaling & Therapeutic Interactions",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Overview</h3>
            <p className="text-sm text-gray-700">
              Nuclear Factor kappa-B (NF-κB) is a master regulator of inflammation, immune response, cell survival. Chronic, dysregulated NF-κB activation drives "inflammaging" and contributes to many age-related diseases.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Therapeutic Interactions (Inhibitors/Modulators)</h3>
            <div className="space-y-2">
              <div>
                <h4 className="font-medium text-green-700 text-sm">KPV (Lys-Pro-Val)</h4>
                <p className="text-xs text-gray-600">Potent NF-κB inhibitor (blocks p65RelA nuclear import).</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">BPC-157 & TB-4</h4>
                <p className="text-xs text-gray-600">Anti-inflammatory actions likely involve NF-κB modulation.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">VIP (Vasoactive Intestinal Peptide)</h4>
                <p className="text-xs text-gray-600">Inhibits pro-inflammatory cytokines, negatively regulates NF-κB.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Estrogen</h4>
                <p className="text-xs text-gray-600">Can modulate NF-κB (e.g., by enhancing IκBα levels).</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Melatonin</h4>
                <p className="text-xs text-gray-600">Anti-inflammatory effects partly via NF-κB modulation.</p>
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Plasmalogens</h4>
                <p className="text-xs text-gray-600">Supplementation reduced NF-κB in a neurotoxicity model.</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Clinical Relevance</h3>
            <p className="text-sm text-gray-700 mb-2">
              Targeting NF-κB is a key strategy to combat inflammaging.
            </p>
            <p className="text-sm text-gray-700">
              <strong>Biomarkers:</strong> hs-CRP, ESR, IL-6, TNF-α.
            </p>
          </div>
        </div>
      )
    },

    // Slide 18: Clinical Application: Testing & Interpretation
    {
      title: "Clinical Application: Testing & Interpretation",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Functional Medicine Approach</h3>
            <p className="text-sm text-gray-700">
              Comprehensive assessment to identify root causes and imbalances.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Advanced Biomarker Testing (Beyond Standard Labs)</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Hormones:</strong> Serum, saliva, urine (e.g., DUTCH) – including precursors, metabolites</li>
              <li>• <strong>Inflammation:</strong> hs-CRP, ESR, cytokines (IL-6, TNF-α), fibrinogen</li>
              <li>• <strong>Metabolic:</strong> Fasting glucose/insulin, HbA1c, HOMA-IR, advanced lipid panels (NMR), ApoB/A1</li>
              <li>• <strong>Nutrients/Cellular Health:</strong> Vitamins (D, B-vitamins), minerals (Mg, Zn, Se), Omega-3 Index, homocysteine, plasmalogens</li>
              <li>• <strong>Oxidative Stress:</strong> GGT, lipid peroxides, 8-OHdG</li>
              <li>• <strong>Genetics:</strong> SNPs related to aging pathways, detoxification, methylation</li>
              <li>• <strong>Gut Health:</strong> Microbiome analysis, intestinal permeability markers</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Interpretation</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Focus on <strong>optimal physiological ranges</strong> vs. standard "normal" ranges</li>
              <li>• Correlate with symptoms and individual patient context</li>
              <li>• Identify patterns and interconnected dysfunctions</li>
            </ul>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Example Longevity Biomarker Panel Categories</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 px-2 font-medium">Category</th>
                    <th className="text-left py-2 px-2 font-medium">Examples</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Hormonal</td>
                    <td className="py-2 px-2">Full thyroid (TSH, fT3, fT4, rT3, Ab), Sex hormones (T, E2, P4, DHEA-S, Preg), IGF-1, Cortisol (diurnal)</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Inflammatory</td>
                    <td className="py-2 px-2">hs-CRP, IL-6, TNF-α, fibrinogen, homocysteine</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Metabolic</td>
                    <td className="py-2 px-2">Fasting Glucose/Insulin, HbA1c, HOMA-IR, Advanced Lipids (NMR), ApoB/A1</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 px-2 font-medium">Nutrient/Cellular</td>
                    <td className="py-2 px-2">Vit D, B12, Folate, Mg, RBC minerals, Omega-3 Index, Plasmalogens</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-2 font-medium">Oxidative Stress</td>
                    <td className="py-2 px-2">GGT, Lipid Peroxides, 8-OHdG, Glutathione</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )
    },

    // Slide 19: Clinical Application: Therapy Selection & Monitoring
    {
      title: "Clinical Application: Therapy Selection & Monitoring",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Personalized "N-of-1" Approach</h3>
            <p className="text-sm text-gray-700">
              Therapy is highly individualized based on comprehensive assessment, genetics, lifestyle, and patient goals.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Therapy Selection Hierarchy</h3>
            <ol className="text-sm space-y-1 text-gray-700 list-decimal list-inside">
              <li><strong>Foundational Lifestyle:</strong> Diet, exercise, sleep, stress management</li>
              <li><strong>Targeted Nutraceuticals & Botanicals</strong></li>
              <li><strong>Hormone Optimization:</strong> As indicated by testing and symptoms</li>
              <li><strong>Peptide Therapies:</strong> For specific goals (e.g., repair, immune, metabolic)</li>
              <li><strong>Regenerative Therapies:</strong> For specific conditions (e.g., OA, significant tissue injury)</li>
            </ol>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Dosing & Administration</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• "Start low, go slow." Titrate to clinical effect and biomarker changes</li>
              <li>• <strong>Peptides:</strong> Specific protocols, often SC (e.g., CJC-1295/Ipamorelin 100-300mcg each SC QHS; BPC-157 250-500mcg QD-BID SC/oral)</li>
              <li>• <strong>Hormones:</strong> Aim for youthful physiological levels. Route matters (e.g., transdermal E2, oral micronized P4)</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Monitoring & Follow-Up</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Regular reassessment (every 8-12 weeks initially)</li>
              <li>• Track both subjective (symptoms, quality of life) and objective (biomarkers) improvements</li>
              <li>• Adjust therapies based on response and tolerance</li>
              <li>• Long-term safety monitoring especially for hormones and peptides</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 20: Safety Considerations & Contraindications
    {
      title: "Safety Considerations & Contraindications",
      content: (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-red-800 mb-3">General Safety Principles</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Comprehensive medical history and physical examination required</li>
              <li>• Screen for contraindications before initiating therapy</li>
              <li>• Regular monitoring and follow-up essential</li>
              <li>• Patient education on proper administration and potential side effects</li>
            </ul>
          </div>

          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-orange-800 mb-3">Peptide-Specific Considerations</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>GHS:</strong> Avoid in active cancer, severe heart failure. Monitor IGF-1 levels</li>
              <li>• <strong>BPC-157:</strong> Not FDA approved for human use. Research compound only</li>
              <li>• <strong>TB-500:</strong> WADA prohibited substance. Caution in competitive athletes</li>
              <li>• Monitor injection sites for local reactions</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Hormone-Specific Considerations</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• <strong>Testosterone:</strong> Screen for prostate cancer, severe BPH, untreated sleep apnea</li>
              <li>• <strong>Estrogen:</strong> History of breast cancer, active VTE, active liver disease</li>
              <li>• <strong>Growth Hormone:</strong> Active malignancy, diabetic retinopathy, severe carpal tunnel</li>
              <li>• Regular monitoring of relevant cancer markers and cardiovascular risk factors</li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Regenerative Medicine Considerations</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Ensure proper sourcing and quality control of stem cell products</li>
              <li>• Screen for infection risk factors</li>
              <li>• Consider immunological compatibility for allogeneic products</li>
              <li>• Monitor for adverse reactions post-treatment</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 21: Future Directions & Emerging Innovations
    {
      title: "Future Directions & Emerging Innovations",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Next-Generation Peptides</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Enhanced delivery systems (oral bioavailability, targeted delivery)</li>
              <li>• Novel mitochondrial-derived peptides beyond MOTS-c and Humanin</li>
              <li>• Combination peptide therapies for synergistic effects</li>
              <li>• AI-designed peptides for specific aging pathways</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Advanced Regenerative Technologies</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Engineered exosomes with specific therapeutic cargo</li>
              <li>• CRISPR-modified stem cells for enhanced function</li>
              <li>• 3D bioprinting for tissue engineering applications</li>
              <li>• Induced pluripotent stem cells (iPSCs) for personalized medicine</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Precision Longevity Medicine</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Multi-omics integration (genomics, proteomics, metabolomics)</li>
              <li>• AI-driven therapy selection and optimization</li>
              <li>• Real-time biomarker monitoring (wearables, continuous glucose monitors)</li>
              <li>• Personalized aging clocks for intervention timing</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Regulatory & Clinical Development</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• FDA guidance on aging as an indication</li>
              <li>• Standardization of manufacturing and quality control</li>
              <li>• Large-scale longitudinal studies on healthspan outcomes</li>
              <li>• Integration with digital health platforms</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 22: SHIFT Framework Analysis - Advanced Therapeutic Innovations
    {
      title: "Advanced Therapeutics Through the SHIFT Framework Lens",
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
            <h3 className="text-lg font-medium text-blue-800 mb-2">Peptides, Hormones & Regenerative Medicine Mapped to Core Aging Pathways</h3>
            <p className="text-sm text-gray-700">How advanced therapeutics target each SHIFT domain</p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold mr-3">S</div>
                <h3 className="text-md font-medium text-amber-800">Sensing & Metabolic Fuel-Gauging</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Therapeutics:</strong> GLP-1 agonists, Growth hormone secretagogues (GHRP-6, Ipamorelin), Metformin, NAD+ precursors</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Mechanisms:</strong> mTOR modulation, AMPK activation, insulin/IGF-1 pathway optimization, sirtuin activation</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Enhanced metabolic flexibility, improved nutrient sensing, optimized growth factor signaling</p>
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold mr-3">H</div>
                <h3 className="text-md font-medium text-green-800">Homeostatic Defense & Repair</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Therapeutics:</strong> BPC-157, TB-500, Growth hormone, Thymosin α1, Stem cell therapies</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Mechanisms:</strong> Enhanced autophagy, DNA repair activation, mitochondrial biogenesis, cellular stress response</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Accelerated healing, improved cellular resilience, enhanced stress adaptation</p>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold mr-3">I</div>
                <h3 className="text-md font-medium text-red-800">Inflammation & Immune Modulation</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Therapeutics:</strong> LL-37, KPV peptide, Hormone replacement therapy, MSC-derived exosomes</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Mechanisms:</strong> NF-κB pathway modulation, inflammasome regulation, immune system rebalancing</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Reduced chronic inflammation, optimized immune function, decreased inflammaging</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-3">F</div>
                <h3 className="text-md font-medium text-purple-800">Fibrosis & Structural Remodeling</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Therapeutics:</strong> Stem cells, Exosome therapy, GHK-Cu peptide, Collagen peptides, Rapamycin</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Mechanisms:</strong> Extracellular matrix remodeling, senescent cell clearance, tissue regeneration, collagen synthesis</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Improved tissue architecture, reduced fibrosis, enhanced regenerative capacity</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-3">T</div>
                <h3 className="text-md font-medium text-blue-800">Timekeeping & Chronobiology</h3>
              </div>
              <p className="text-xs text-gray-700 mb-1"><strong>Key Therapeutics:</strong> Melatonin, Growth hormone (timed administration), NAD+ precursors, Circadian hormone optimization</p>
              <p className="text-xs text-gray-700 mb-1"><strong>Mechanisms:</strong> Circadian rhythm entrainment, cellular clock gene regulation, telomere maintenance</p>
              <p className="text-xs text-gray-700"><strong>Clinical Impact:</strong> Optimized biological rhythms, improved sleep-wake cycles, enhanced cellular timing mechanisms</p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-md font-medium text-gray-800 mb-2">Clinical Integration Strategy</h3>
            <p className="text-xs text-gray-700">Advanced therapeutics work synergistically across SHIFT domains. Multi-modal approaches targeting multiple pathways simultaneously offer superior outcomes compared to single-intervention strategies. Personalization based on individual SHIFT domain dysfunction patterns guides optimal therapeutic selection.</p>
          </div>
        </div>
      )
    },

    // Slide 23: Key Takeaways & Clinical Pearls
    {
      title: "Key Takeaways & Clinical Pearls",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-blue-800 mb-3">Core Principles</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Longevity medicine requires a multi-modal, systems-based approach</li>
              <li>• Advanced therapeutics work synergistically to address aging pathways</li>
              <li>• Personalization based on individual biomarkers and genetics is essential</li>
              <li>• Safety monitoring and follow-up are paramount</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-3">Clinical Pearls</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Start with foundational lifestyle interventions before advanced therapies</li>
              <li>• Use physiological rather than supraphysiological dosing</li>
              <li>• Monitor both subjective symptoms and objective biomarkers</li>
              <li>• Consider drug-drug and therapy-therapy interactions</li>
              <li>• Maintain detailed documentation for safety and efficacy tracking</li>
            </ul>
          </div>

          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-purple-800 mb-3">Future Practice Integration</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Develop protocols for patient selection and therapy sequencing</li>
              <li>• Establish monitoring schedules and safety parameters</li>
              <li>• Build referral networks for specialized therapies</li>
              <li>• Stay current with evolving research and regulatory guidance</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-800 mb-3">Patient Education Focus</h3>
            <ul className="text-sm space-y-1 text-gray-700">
              <li>• Set realistic expectations for timeline and outcomes</li>
              <li>• Emphasize lifestyle as the foundation of longevity</li>
              <li>• Educate on proper administration and monitoring</li>
              <li>• Discuss cost-benefit considerations transparently</li>
            </ul>
          </div>
        </div>
      )
    },

    // Slide 24: Summary & Next Steps
    {
      title: "Summary & Next Steps",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h3 className="text-xl font-medium text-blue-800 mb-4 text-center">Advanced Therapeutic Innovations</h3>
            <p className="text-sm text-gray-700 text-center">
              Peptides, hormones, and regenerative medicine represent the cutting edge of longevity interventions, offering unprecedented precision in addressing the fundamental mechanisms of aging.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg text-center">
              <Dna className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <h4 className="font-medium text-green-800 text-sm">Peptides</h4>
              <p className="text-xs text-gray-600">Precision signaling for targeted interventions</p>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg text-center">
              <Activity className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <h4 className="font-medium text-purple-800 text-sm">Hormones</h4>
              <p className="text-xs text-gray-600">Systemic optimization and balance</p>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg text-center">
              <Shield className="w-8 h-8 text-orange-600 mx-auto mb-2" />
              <h4 className="font-medium text-orange-800 text-sm">Regenerative</h4>
              <p className="text-xs text-gray-600">Tissue repair and rejuvenation</p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-3">Next Steps for Clinical Integration</h3>
            <ol className="text-sm space-y-1 text-gray-700 list-decimal list-inside">
              <li>Assess current practice readiness for advanced therapeutics</li>
              <li>Develop comprehensive patient evaluation protocols</li>
              <li>Establish safety monitoring and follow-up procedures</li>
              <li>Build expertise through continuing education and training</li>
              <li>Create patient education materials and consent processes</li>
            </ol>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg text-center">
            <h3 className="text-lg font-medium text-indigo-800 mb-2">The Future of Medicine</h3>
            <p className="text-sm text-gray-700">
              By integrating these advanced therapeutics with personalized assessment and the SHIFT framework, we can optimize healthspan and redefine what it means to age well.
            </p>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <nav className="flex items-center space-x-2 text-sm">
            <Link href="/">
              <Home className="w-4 h-4 text-gray-500 hover:text-gray-700" />
            </Link>
            <span className="text-gray-400">/</span>
            <Link href="/mentorship" className="text-blue-600 hover:text-blue-800">
              Mentorship
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-700">Advanced Therapeutic Innovations</span>
          </nav>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link href="/mentorship" className="inline-flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Mentorship Program
            </Link>
            <LectureDownloader lectureName="Advanced Therapeutic Innovations" fileName="AdvancedTherapeuticInnovations" />
          </div>
          <h1 className="text-3xl font-light text-gray-900 mb-2">Advanced Therapeutic Innovations</h1>
          <p className="text-gray-600 font-light">
            Peptides, Hormones, & Regenerative Medicine for Longevity
          </p>
        </div>

        {/* Slideshow Container */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Slide Content */}
          <div className="h-[70vh] p-8 overflow-y-auto">
            <h2 className="text-2xl font-light text-gray-900 mb-6">{slides[currentSlide].title}</h2>
            {slides[currentSlide].content}
          </div>

          {/* Navigation Controls */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-light"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 font-light">
                {currentSlide + 1} of {slides.length}
              </span>
            </div>

            {/* Slide Indicators */}
            <div className="flex space-x-1">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full ${
                    index === currentSlide ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              disabled={currentSlide === slides.length - 1}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-light"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard Navigation */}
      <div className="fixed bottom-4 right-4 bg-white p-2 rounded-lg shadow-md">
        <p className="text-xs text-gray-500 font-light">Use ← → arrows to navigate</p>
      </div>

      {/* Accompanying Audio - Below Slideshow */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h4 className="text-lg font-medium text-gray-800 mb-4">Accompanying Audio Lecture</h4>
          <div className="bg-gray-50 p-4 rounded-lg">
            <iframe 
              src="https://share.descript.com/embed/eAkETxCrKhN" 
              width="640" 
              height="360" 
              frameBorder="0" 
              allowFullScreen
              className="w-full h-64 md:h-80 rounded"
              title="Advanced Therapeutic Innovations Audio Lecture"
            ></iframe>
          </div>
          <p className="text-sm text-gray-600 mt-3 font-light">
            Play the audio while navigating through slides for the complete learning experience.
          </p>
        </div>
      </div>
    </div>
  );
}